/**
 * Transparent Pixi layer for TrixChar containerFront (claw + trick VFX) above the DOM hub.
 * Same ticker model as the back wheel app — Spine drives visibility, no manual claw gating.
 */

import { Application, Container } from "pixi.js"
import {
  safeDestroyPixiApplication,
  safeStopPixiApplication,
} from "./pixiTeardown"

export class WheelEffectsLayer {
  private _app: Application | null = null
  private _padLeft = 0
  private _padTop = 0
  private _destroyed = false

  get app(): Application | null {
    return this._app
  }

  async create(
    size: number,
    padLeft: number,
    padTop: number
  ): Promise<Application> {
    this._destroyed = false
    if (this._app) return this._app
    this._padLeft = padLeft
    this._padTop = padTop
    const app = new Application()
    await app.init({
      width: size + padLeft,
      height: size + padTop,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      autoStart: true,
    })
    this._app = app
    this.styleCanvas(app.canvas)
    return app
  }

  start(): void {
    const app = this._app
    if (!app?.renderer || app.ticker.started) return
    app.start()
  }

  mountRoot(root: Container): void {
    const app = this._app
    if (!app) return
    if (root.parent !== app.stage) {
      app.stage.removeChildren()
      app.stage.addChild(root)
    }
  }

  resize(size: number, padLeft: number, padTop: number): void {
    const app = this._app
    if (!app) return
    this._padLeft = padLeft
    this._padTop = padTop
    app.renderer.resize(size + padLeft, size + padTop)
    this.styleCanvas(app.canvas)
  }

  attachTo(mountEl: HTMLElement, zIndex = 20): void {
    const canvas = this._app?.canvas
    if (!canvas) return
    this.styleCanvas(canvas, zIndex)
    if (canvas.parentElement !== mountEl) {
      canvas.remove()
      mountEl.appendChild(canvas)
    }
  }

  detach(): void {
    if (this._destroyed) return
    this._app?.canvas?.remove()
    safeStopPixiApplication(this._app)
  }

  destroy(): void {
    if (this._destroyed) return
    this._destroyed = true
    const app = this._app
    this._app = null
    safeDestroyPixiApplication(app)
  }

  private styleCanvas(canvas: HTMLCanvasElement, zIndex = 20): void {
    canvas.style.backgroundColor = "transparent"
    canvas.style.pointerEvents = "none"
    canvas.style.display = "block"
    canvas.style.position = "absolute"
    canvas.style.top = `-${this._padTop}px`
    canvas.style.left = `-${this._padLeft}px`
    canvas.style.zIndex = String(zIndex)
  }
}
